/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { combineData } from '@amazon/vinyl-observable'
import { jsx } from '@amazon/vinyl-tsx'
import { dismissStack, undoLast } from '@/blackbolt/dismissStack'
import { mutationUndoStack, undoLastMutation } from '@/components/mutationUndoStack'
import { getString } from '@/blackbolt/i18n/bundle.generated'

/**
 * Toast source discriminant. Two independent stacks feed this toast:
 *
 * - The feed's card-dismiss stack (`blackbolt/dismissStack`) — a
 *   dismissed card can be re-inserted via Undo.
 * - The shared mutation stack (`components/mutationUndoStack`) — used
 *   by library / like / follow / add-to-playlist / add-to-queue
 *   mutations across the app.
 *
 * When both stacks have entries the mutation stack wins — those are
 * transient action confirmations, so the newer verb dominates until
 * it times out. The dismiss stack re-appears once the mutation entry
 * evicts.
 */
type UndoSource =
    | { readonly kind: 'dismiss'; readonly message: string }
    | { readonly kind: 'mutation'; readonly message: string; readonly count: number }

/**
 * Fixed-position "Card removed / Added to library / … — Undo" toast.
 * Bound to two independent observables:
 *
 *   1. `dismissStack` — feed card dismissals; `undoLast` re-inserts.
 *   2. `mutationUndoStack` — batch mutation confirmations
 *      (like / follow / add-to-playlist / add-to-queue);
 *      `undoLastMutation` fires the chained undo callback.
 *
 * Visible whenever either stack is non-empty; hidden when both are
 * empty. The rendered message + Undo click route through the source
 * that owns the tail entry.
 */
export function UndoToast() {
    const source$ = combineData({ dismissStack, mutationUndoStack }).map(
        ({ dismissStack: dismissed, mutationUndoStack: mutations }): UndoSource | null => {
            // Mutation stack wins when both are populated — see the
            // discriminant comment above.
            const mTail = mutations.at(-1)
            if (mTail) {
                // Only decorate when the count grew via batching. A caller
                // that passed count > 1 up front (e.g. "Added 5 tracks to
                // <playlist>") already reads pluralized — appending `(×N)`
                // would double-count.
                const message = mTail.mergedFromRepeat
                    ? formatCountedMessage(mTail.message, mTail.count)
                    : mTail.message
                return { kind: 'mutation', message, count: mTail.count }
            }
            if (dismissed.length > 0) return { kind: 'dismiss', message: 'Card removed' }
            return null
        }
    )
    const hasEntry = source$.map((s) => s !== null)
    // Hold the last message through the container's leave transition —
    // clearing it when source goes null would blank the text a frame before
    // the pill finishes fading, leaving only the static Undo label visible.
    let lastMessage = ''
    const message$ = source$.map((s) => {
        if (s) lastMessage = s.message
        return lastMessage
    })
    return (
        <div
            className="undoToast"
            classList={[hasEntry.map((v) => (v ? 'undoToast--visible' : null))]}
            role="status"
            aria-live="polite"
            onConnect={(el) => {
                // `inert` alone drops the hidden toast from the a11y tree and
                // blurs its Undo button — a bare aria-hidden can't blur, so it
                // trips the browser's focused-descendant warning on hide.
                return hasEntry.onData((visible) => {
                    if (visible) el.removeAttribute('inert')
                    else el.setAttribute('inert', '')
                })
            }}
        >
            <span className="undoToastMessage">{message$}</span>
            <button
                type="button"
                className="undoToastAction"
                onclick={() => {
                    // Pop from whichever stack is currently on top —
                    // mirror the same precedence used to render the
                    // message so the Undo button acts on the visible
                    // entry.
                    if (mutationUndoStack.value.length > 0) {
                        undoLastMutation()
                    } else {
                        undoLast()
                    }
                }}
            >
                {getString('dmusic_feed_undo_action')}
            </button>
        </div>
    )
}

/**
 * Suffix a batch count onto the base message. Kept as a plain string
 * append (rather than a per-caller `formatMessage` hook) so the
 * batching layer stays copy-agnostic — the caller doesn't have to
 * know its message will be pluralized.
 */
function formatCountedMessage(message: string, count: number): string {
    return `${message} (×${count})`
}
